import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../api/supabase.js';

/**
 * AuthContext shape:
 *   session         — Supabase session object (or null)
 *   user            — Supabase auth user (or null)
 *   profile         — Row from public.users table, OR null if the
 *                     fetch completed with no matching row.
 *   role            — Convenience pointer to profile.role (or null)
 *   loading         — true during initial app boot (before we know
 *                     if a session exists at all).
 *   profileLoading  — true while a profile fetch is in flight. Distinct
 *                     from `loading` so the UI can differentiate "we're
 *                     still booting" from "we have a session, fetching
 *                     your row".
 *   signIn(email, password)
 *   signOut()
 *   refreshProfile()
 *
 * Loading-state contract for consumers:
 *   - loading=true                          -> show full-screen loader
 *   - loading=false, no session             -> redirect to /login
 *   - loading=false, session, profileLoading -> show full-screen loader
 *   - loading=false, session, profile=null  -> "Approval pending"
 *                                              (the trigger didn't create
 *                                              a row, or it was deleted)
 *   - loading=false, session, profile.status!=='active'  -> "Account inactive"
 *   - loading=false, session, profile.role mismatch      -> redirect to own dashboard
 *   - loading=false, session, profile fully valid        -> render children
 */

const AuthContext = createContext(null);

const PROFILE_FETCH_TIMEOUT_MS = 8000;

function withTimeout(promise, ms, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`Timeout after ${ms}ms: ${label}`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Stale-response guard
  const latestFetchIdRef = useRef(0);

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      setProfileLoading(false);
      return null;
    }

    const fetchId = ++latestFetchIdRef.current;
    setProfileLoading(true);

    try {
      const query = supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      const { data, error } = await withTimeout(
        query,
        PROFILE_FETCH_TIMEOUT_MS,
        'fetchProfile',
      );

      // Discard stale results
      if (fetchId !== latestFetchIdRef.current) return null;

      if (error) {
         
        console.error('[ORIN] Failed to fetch profile:', error.message);
        setProfile(null);
        return null;
      }

      setProfile(data ?? null);
      return data ?? null;
    } catch (err) {
      if (fetchId !== latestFetchIdRef.current) return null;
       
      console.error('[ORIN] fetchProfile error:', err.message || err);
      setProfile(null);
      return null;
    } finally {
      // Only the latest fetch should clear the loading flag
      if (fetchId === latestFetchIdRef.current) {
        setProfileLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        if (!mounted) return;

        setSession(initialSession);
        setUser(initialSession?.user ?? null);

        if (initialSession?.user) {
          await fetchProfile(initialSession.user.id);
        }
      } catch (err) {
         
        console.error('[ORIN] Initial session restore failed:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) return;
        if (event === 'INITIAL_SESSION') return; // handled by getSession() above

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          // Fire and forget; fetchProfile manages its own state.
          fetchProfile(newSession.user.id);
        } else {
          // Sign-out path
          setProfile(null);
          setProfileLoading(false);
          latestFetchIdRef.current++; // invalidate any in-flight fetch
        }
      },
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error };
    // onAuthStateChange will pick up the new session and trigger fetchProfile.
    return { data };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.id) return fetchProfile(user.id);
    return null;
  }, [user?.id, fetchProfile]);

  const value = {
    session,
    user,
    profile,
    role: profile?.role ?? null,
    loading,
    profileLoading,
    signIn,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}